import { LucideIcon, Users, Zap, Building2, Crown } from 'lucide-react'

export interface PricingPlan {
  id: string
  name: string
  description: string
  icon: LucideIcon
  color: string
  price: number | 'custom'
  priceYearly: number | 'custom'
  priceLabel?: string
  teamSize: string
  highlighted: boolean
  cta: string
  ctaVariant: 'default' | 'outline'
  features: {
    category: string
    items: { name: string; included: boolean | string }[]
  }[]
}

export interface PricingFAQ {
  question: string
  answer: string
}

export const pricingPlans: PricingPlan[] = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'Perfect for small teams getting started with document automation.',
    icon: Users,
    color: 'from-blue-400 to-blue-600',
    price: 15,
    priceYearly: 12,
    teamSize: 'Up to 5 users',
    highlighted: false,
    cta: 'Start free trial',
    ctaVariant: 'outline',
    features: [
      {
        category: 'Core Features',
        items: [
          { name: 'Up to 5 team members', included: true },
          { name: '10 templates', included: true },
          { name: '50 AI generations/month', included: true },
          { name: '5 GB storage', included: true },
          { name: 'Email support', included: true },
        ],
      },
      {
        category: 'Document Features',
        items: [
          { name: 'Word & Excel add-ins', included: true },
          { name: 'Template library access', included: true },
          { name: 'Basic brand controls', included: true },
          { name: 'PDF export', included: true },
          { name: 'Version history (30 days)', included: true },
        ],
      },
      {
        category: 'Advanced Features',
        items: [
          { name: 'PowerPoint add-in', included: false },
          { name: 'Outlook add-in', included: false },
          { name: 'Custom workflows', included: false },
          { name: 'API access', included: false },
          { name: 'SSO / SAML', included: false },
        ],
      },
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'For growing teams that need unlimited templates and AI power.',
    icon: Zap,
    color: 'from-violet-400 to-purple-600',
    price: 29,
    priceYearly: 24,
    teamSize: '5-100 users',
    highlighted: true,
    cta: 'Start free trial',
    ctaVariant: 'default',
    features: [
      {
        category: 'Core Features',
        items: [
          { name: '5-100 team members', included: true },
          { name: 'Unlimited templates', included: true },
          { name: 'Unlimited AI generations', included: true },
          { name: '50 GB storage', included: true },
          { name: 'Priority email support', included: true },
        ],
      },
      {
        category: 'Document Features',
        items: [
          { name: 'Word & Excel add-ins', included: true },
          { name: 'PowerPoint add-in', included: true },
          { name: 'Outlook add-in', included: true },
          { name: 'Advanced brand controls', included: true },
          { name: 'Version history (1 year)', included: true },
        ],
      },
      {
        category: 'Advanced Features',
        items: [
          { name: 'Custom workflows', included: true },
          { name: 'API access', included: true },
          { name: 'HubSpot CRM connector', included: true },
          { name: 'Analytics dashboard', included: true },
          { name: 'SSO / SAML', included: false },
        ],
      },
    ],
  },
  {
    id: 'business',
    name: 'Business',
    description: 'Enterprise-grade security and governance for larger organizations.',
    icon: Building2,
    color: 'from-amber-400 to-orange-600',
    price: 49,
    priceYearly: 41,
    teamSize: '20-500 users',
    highlighted: false,
    cta: 'Contact sales',
    ctaVariant: 'outline',
    features: [
      {
        category: 'Core Features',
        items: [
          { name: '20-500 team members', included: true },
          { name: 'Unlimited templates', included: true },
          { name: 'Unlimited AI generations', included: true },
          { name: '500 GB storage', included: true },
          { name: 'Dedicated support manager', included: true },
        ],
      },
      {
        category: 'Document Features',
        items: [
          { name: 'All Office add-ins', included: true },
          { name: 'Enterprise brand management', included: true },
          { name: 'Document compliance checks', included: true },
          { name: 'Audit trails', included: true },
          { name: 'Unlimited version history', included: true },
        ],
      },
      {
        category: 'Advanced Features',
        items: [
          { name: 'SSO / SAML', included: true },
          { name: 'Advanced governance', included: true },
          { name: 'Custom integrations', included: true },
          { name: 'Advanced analytics', included: true },
          { name: 'Role-based permissions', included: true },
        ],
      },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Custom solutions for organizations with specific compliance needs.',
    icon: Crown,
    color: 'from-emerald-400 to-green-600',
    price: 'custom',
    priceYearly: 'custom',
    priceLabel: 'Custom pricing',
    teamSize: 'Unlimited users',
    highlighted: false,
    cta: 'Contact sales',
    ctaVariant: 'outline',
    features: [
      {
        category: 'Core Features',
        items: [
          { name: 'Unlimited team members', included: true },
          { name: 'Unlimited everything', included: true },
          { name: 'Unlimited AI generations', included: true },
          { name: 'Unlimited storage', included: true },
          { name: '24/7 dedicated support', included: true },
        ],
      },
      {
        category: 'Document Features',
        items: [
          { name: 'All Office add-ins', included: true },
          { name: 'White-label option', included: true },
          { name: 'Custom compliance workflows', included: true },
          { name: 'Legal document automation', included: true },
          { name: 'Perpetual version history', included: true },
        ],
      },
      {
        category: 'Enterprise Features',
        items: [
          { name: 'On-premise deployment option', included: true },
          { name: 'HIPAA BAA available', included: true },
          { name: 'SOC 2 Type II certified', included: true },
          { name: 'Custom SLA', included: true },
          { name: 'Dedicated success team', included: true },
        ],
      },
    ],
  },
]

export const featureComparison = {
  categories: [
    {
      name: 'Team & Collaboration',
      features: [
        { name: 'Team members', starter: 'Up to 5', professional: '5-100', business: '20-500', enterprise: 'Unlimited' },
        { name: 'Shared templates', starter: true, professional: true, business: true, enterprise: true },
        { name: 'Team folders', starter: true, professional: true, business: true, enterprise: true },
        { name: 'Role-based access', starter: false, professional: 'Basic', business: 'Advanced', enterprise: 'Custom' },
        { name: 'Guest access', starter: false, professional: true, business: true, enterprise: true },
      ],
    },
    {
      name: 'Templates & Documents',
      features: [
        { name: 'Template limit', starter: '10', professional: 'Unlimited', business: 'Unlimited', enterprise: 'Unlimited' },
        { name: 'Version history', starter: '30 days', professional: '1 year', business: 'Unlimited', enterprise: 'Perpetual' },
        { name: 'Brand controls', starter: 'Basic', professional: 'Advanced', business: 'Enterprise', enterprise: 'Custom' },
        { name: 'Clause libraries', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Document approval workflows', starter: false, professional: 'Basic', business: 'Advanced', enterprise: 'Custom' },
      ],
    },
    {
      name: 'AI & Automation',
      features: [
        { name: 'AI generations/month', starter: '50', professional: 'Unlimited', business: 'Unlimited', enterprise: 'Unlimited' },
        { name: 'AI document drafting', starter: true, professional: true, business: true, enterprise: true },
        { name: 'AI formula assistant (Excel)', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Custom AI models', starter: false, professional: false, business: true, enterprise: true },
        { name: 'AI training on your data', starter: false, professional: false, business: false, enterprise: true },
      ],
    },
    {
      name: 'Office Add-ins',
      features: [
        { name: 'Word add-in', starter: true, professional: true, business: true, enterprise: true },
        { name: 'Excel add-in', starter: true, professional: true, business: true, enterprise: true },
        { name: 'PowerPoint add-in', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Outlook add-in', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Add-in customization', starter: false, professional: false, business: 'Logo', enterprise: 'Full' },
      ],
    },
    {
      name: 'Storage & Exports',
      features: [
        { name: 'Storage included', starter: '5 GB', professional: '50 GB', business: '500 GB', enterprise: 'Unlimited' },
        { name: 'PDF export', starter: true, professional: true, business: true, enterprise: true },
        { name: 'Bulk export', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Custom export formats', starter: false, professional: false, business: true, enterprise: true },
      ],
    },
    {
      name: 'Integrations',
      features: [
        { name: 'HubSpot', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Microsoft 365', starter: true, professional: true, business: true, enterprise: true },
        { name: 'API access', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Custom integrations', starter: false, professional: false, business: true, enterprise: true },
      ],
    },
    {
      name: 'Security & Compliance',
      features: [
        { name: 'SSO / SAML', starter: false, professional: false, business: true, enterprise: true },
        { name: 'Audit logs', starter: false, professional: 'Basic', business: 'Advanced', enterprise: 'Full' },
        { name: 'SOC 2 Type II', starter: true, professional: true, business: true, enterprise: true },
        { name: 'HIPAA compliance', starter: false, professional: false, business: false, enterprise: true },
        { name: 'On-premise deployment', starter: false, professional: false, business: false, enterprise: true },
      ],
    },
    {
      name: 'Support',
      features: [
        { name: 'Email support', starter: true, professional: true, business: true, enterprise: true },
        { name: 'Priority support', starter: false, professional: true, business: true, enterprise: true },
        { name: 'Dedicated manager', starter: false, professional: false, business: true, enterprise: true },
        { name: 'Phone support', starter: false, professional: false, business: true, enterprise: true },
        { name: 'Custom SLA', starter: false, professional: false, business: false, enterprise: true },
      ],
    },
  ],
}

export const pricingFAQs: PricingFAQ[] = [
  {
    question: 'Can I try Templatean before committing?',
    answer: 'Yes! We offer a 14-day free trial on all plans with no credit card required. You can also take advantage of our 30-day satisfaction guarantee—if you\'re not completely satisfied, we\'ll refund your payment, no questions asked.',
  },
  {
    question: 'How does the per-user pricing work?',
    answer: 'You pay for each active user on your account. Active users are team members who have logged in within the last 30 days. You can add or remove users at any time, and billing is prorated automatically.',
  },
  {
    question: 'What happens if I exceed my AI generation limit?',
    answer: 'On the Starter plan, you\'ll receive a notification when you reach 80% of your limit. You can upgrade to Professional for unlimited AI generations, or purchase additional generation packs at $0.10 per generation.',
  },
  {
    question: 'Can I switch between monthly and annual billing?',
    answer: 'Yes, you can switch at any time. When switching from monthly to annual, you\'ll immediately get the discounted rate. When switching from annual to monthly, the change takes effect at your next renewal date.',
  },
  {
    question: 'Do you offer discounts for nonprofits or educational institutions?',
    answer: 'Yes! We offer 50% off for verified nonprofits and educational institutions. Contact our sales team with proof of nonprofit or educational status to receive your discount code.',
  },
  {
    question: 'What\'s included in the free trial?',
    answer: 'Your 14-day trial includes full access to Professional plan features, including unlimited AI generations, all Office add-ins, and API access. No credit card required to start.',
  },
  {
    question: 'How secure is my data?',
    answer: 'Security is our top priority. We\'re SOC 2 Type II certified, use AES-256 encryption at rest and TLS 1.3 in transit, and never train AI models on your data without explicit consent. Enterprise customers can also deploy on-premise.',
  },
  {
    question: 'Can I cancel my subscription at any time?',
    answer: 'Yes, you can cancel your subscription at any time from your account settings. You\'ll retain access to your current plan until the end of your billing period. We don\'t believe in lock-in—your data is always yours to export.',
  },
]
